import { memo, useState, FC, useMemo } from 'react';
import { has, isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import UI Components
import ConfirmationModal from 'components/common/Modals/ConfirmationModal';
import { CustomerJourneyInfoModal, ConfirmationModalWithWarning } from 'components/CustomerJourneyModal';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Constants
import {
    ModalsActions,
    l,
    ActionsIcons,
    ModalsClassNames,
    PromotionTypesLabels,
    RealtimePromotionTypes,
} from 'constants/common';
// Import Services
import { PromotionHttpService, CustomerJourneyHttpService } from 'services/http';
// Import Helpers
import { Helpers } from 'services';
import { modalsStateSetDictKeys } from './config';

const { getUsedInConfirmationText } = Helpers;

interface Props {
    isDeleteModalOpenedState: boolean;
    isUsedInConfirmationModalOpenedState: boolean;
    isArchiveModalOpenedState: boolean;
    isUnArchiveModalOpenedState: boolean;
    isStopSendModalOpenedState: boolean;
    isStartCustomerJourneyModalOpenedState: boolean;
    isResumeModalOpenedState: boolean;
    isInfoModalOpenedState: boolean;
    isMakePublicModalOpenedState?: boolean;
    isMakePrivateModalOpenedState?: boolean;
    modalsStateSetDict: Record<string, { key: string }>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    openedModalData: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    goToEditCustomerJourney?: (workflow: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleInfoModalActionClick?: (key?: string, workflow?: Record<string, any>) => void;
    onCloseModalHandler: (
        key: string,
        action?: string,
        alertType?: string,
        alertMessage?: Nullable<string>,
        count?: number,
    ) => void;
}

// TODO: change all actions with service with doAction methods
const CustomerJourneyActionsModals: FC<Props> = (props) => {
    const {
        isDeleteModalOpenedState,
        isUsedInConfirmationModalOpenedState,
        isArchiveModalOpenedState,
        isUnArchiveModalOpenedState,
        isStopSendModalOpenedState,
        isStartCustomerJourneyModalOpenedState,
        isResumeModalOpenedState,
        isInfoModalOpenedState,
        isMakePublicModalOpenedState,
        isMakePrivateModalOpenedState,
        modalsStateSetDict,
        openedModalData,
        goToEditCustomerJourney,
        handleInfoModalActionClick,
        onCloseModalHandler,
    } = props;
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation();
    const { doPostRequest, doDeleteRequest } = useRequest();

    const isActionCampaign = openedModalData?.PromotionType === +RealtimePromotionTypes.ActionCampaign;

    const {
        archiveCustomerJourneyRequest,
        archiveCustomerJourneysRequest,
        unarchiveCustomerJourneyRequest,
        unarchiveCustomerJourneysRequest,
        deleteCustomerJourneyRequest,
        deleteCustomerJourneysRequest,
        stopSendCustomerJourneyRequest,
        startCustomerJourneyRequest,
        resumeCustomerJourneyRequest,
        setPromotionIsPublicRequest,
    } = useCancelableRequests({
        deleteCustomerJourneyRequest: CustomerJourneyHttpService.deleteCustomerJourney,
        deleteCustomerJourneysRequest: CustomerJourneyHttpService.deleteCustomerJourneys,
        archiveCustomerJourneyRequest: CustomerJourneyHttpService.archiveCustomerJourney,
        archiveCustomerJourneysRequest: CustomerJourneyHttpService.archiveCustomerJourneys,
        unarchiveCustomerJourneyRequest: CustomerJourneyHttpService.unarchiveCustomerJourney,
        unarchiveCustomerJourneysRequest: CustomerJourneyHttpService.unarchiveCustomerJourneys,
        stopSendCustomerJourneyRequest: CustomerJourneyHttpService.stopSendCustomerJourney,
        startCustomerJourneyRequest: CustomerJourneyHttpService.startCustomerJourney,
        resumeCustomerJourneyRequest: CustomerJourneyHttpService.resumeCustomerJourney,
        setPromotionIsPublicRequest: PromotionHttpService.setPromotionIsPublic,
    });

    const deleteCustomerJourney = () => {
        setIsLoading(true);
        doDeleteRequest(deleteCustomerJourneyRequest.request, {
            queryString: { workflowId: openedModalData.WorkflowId },
            isShowError: false,
        }).then(({ AlertMessage, AlertType, IsCanceled }) => {
            // Aborted on unmount: do not close the modal or show a toaster
            if (IsCanceled) return;
            onCloseModalHandler(
                modalsStateSetDict.deleteCustomerJourney.key,
                ModalsActions.DELETE,
                AlertType,
                AlertMessage,
            );
            setIsLoading(false);
        });
    };

    const deleteCustomerJourneys = () => {
        setIsLoading(true);
        const { allowDeleteCustomerJourneys } = openedModalData;
        if (isEmpty(allowDeleteCustomerJourneys)) {
            return onCloseModalHandler(modalsStateSetDict.deleteCustomerJourney.key);
        }
        const allowDeleteCustomerJourneysIds = allowDeleteCustomerJourneys.map(({ workflowId }) => {
            return workflowId;
        });

        doPostRequest(deleteCustomerJourneysRequest.request, {
            requestBody: allowDeleteCustomerJourneysIds,
            isShowError: false,
        }).then(({ AlertMessage, AlertType, IsCanceled }) => {
            // Aborted on unmount: do not close the modal or show a toaster
            if (IsCanceled) return;
            onCloseModalHandler(
                modalsStateSetDict.deleteCustomerJourney.key,
                ModalsActions.DELETE,
                AlertType,
                AlertMessage,
                allowDeleteCustomerJourneys.length,
            );
            setIsLoading(false);
        });
    };

    const archiveCustomerJourney = () => {
        setIsLoading(true);
        doPostRequest(archiveCustomerJourneyRequest.request, {
            requestBody: null,
            queryString: { workflowId: openedModalData.WorkflowId },
            isShowError: false,
        }).then(({ AlertMessage, AlertType, IsCanceled }) => {
            // Aborted on unmount: do not close the modal or show a toaster
            if (IsCanceled) return;
            onCloseModalHandler(
                modalsStateSetDict.archiveCustomerJourney.key,
                ModalsActions.ARCHIVE,
                AlertType,
                AlertMessage,
            );
            setIsLoading(false);
        });
    };

    const archiveCustomerJourneys = () => {
        setIsLoading(true);
        const { allowArchiveCustomerJourneys } = openedModalData;
        if (isEmpty(allowArchiveCustomerJourneys)) {
            return onCloseModalHandler(modalsStateSetDict.archiveCustomerJourney.key);
        }
        const allowArchiveCustomerJourneysIds = allowArchiveCustomerJourneys.map(({ workflowId }) => {
            return workflowId;
        });
        doPostRequest(archiveCustomerJourneysRequest.request, {
            requestBody: allowArchiveCustomerJourneysIds,
            isShowError: false,
        }).then(({ AlertMessage, AlertType, IsCanceled }) => {
            // Aborted on unmount: do not close the modal or show a toaster
            if (IsCanceled) return;
            onCloseModalHandler(
                modalsStateSetDict.archiveCustomerJourney.key,
                ModalsActions.ARCHIVE,
                AlertType,
                AlertMessage,
                allowArchiveCustomerJourneys.length,
            );
            setIsLoading(false);
        });
    };

    const unarchiveCustomerJourney = () => {
        setIsLoading(true);
        doPostRequest(unarchiveCustomerJourneyRequest.request, {
            requestBody: null,
            queryString: { workflowId: openedModalData.WorkflowId },
            isShowError: false,
        }).then(({ AlertMessage, AlertType, IsCanceled }) => {
            // Aborted on unmount: do not close the modal or show a toaster
            if (IsCanceled) return;
            onCloseModalHandler(
                modalsStateSetDict.unarchiveCustomerJourney.key,
                ModalsActions.UNARCHIVE,
                AlertType,
                AlertMessage,
            );
            setIsLoading(false);
        });
    };

    const unarchiveCustomerJourneys = () => {
        setIsLoading(true);
        const { allowUnarchiveCustomerJourneys } = openedModalData;
        if (isEmpty(allowUnarchiveCustomerJourneys)) {
            return onCloseModalHandler(modalsStateSetDict.archiveSegment.key);
        }
        const allowUnarchiveCustomerJourneysIds = allowUnarchiveCustomerJourneys.map(({ workflowId }) => {
            return workflowId;
        });
        doPostRequest(unarchiveCustomerJourneysRequest.request, {
            requestBody: allowUnarchiveCustomerJourneysIds,
            isShowError: false,
        }).then(({ AlertMessage, AlertType, IsCanceled }) => {
            // Aborted on unmount: do not close the modal or show a toaster
            if (IsCanceled) return;
            onCloseModalHandler(
                modalsStateSetDict.unarchiveCustomerJourney.key,
                ModalsActions.UNARCHIVE,
                AlertType,
                AlertMessage,
                allowUnarchiveCustomerJourneys.length,
            );
            setIsLoading(false);
        });
    };

    const stopSendCustomerJourney = () => {
        setIsLoading(true);
        doPostRequest(stopSendCustomerJourneyRequest.request, {
            requestBody: null,
            queryString: { workflowId: openedModalData.WorkflowId },
            isShowError: false,
        }).then(({ AlertMessage, AlertType, IsCanceled }) => {
            // Aborted on unmount: do not close the modal or show a toaster
            if (IsCanceled) return;
            onCloseModalHandler(
                modalsStateSetDict.stopSendCustomerJourney.key,
                ModalsActions.CUSTOMER_JOURNEY_STOP,
                AlertType,
                AlertMessage,
            );
            setIsLoading(false);
        });
    };

    const startCustomerJourney = () => {
        setIsLoading(true);
        doPostRequest(startCustomerJourneyRequest.request, {
            requestBody: null,
            queryString: { workflowId: openedModalData.WorkflowId },
            isShowError: false,
        }).then(({ AlertMessage, AlertType, IsCanceled }) => {
            // Aborted on unmount: do not close the modal or show a toaster
            if (IsCanceled) return;
            onCloseModalHandler(
                modalsStateSetDict.startCustomerJourney.key,
                ModalsActions.START,
                AlertType,
                AlertMessage,
            );
            setIsLoading(false);
        });
    };

    const resumeCustomerJourney = () => {
        setIsLoading(true);
        doPostRequest(resumeCustomerJourneyRequest.request, {
            requestBody: null,
            queryString: { workflowId: openedModalData.WorkflowId },
            isShowError: false,
        }).then(({ AlertMessage, AlertType, IsCanceled }) => {
            // Aborted on unmount: do not close the modal or show a toaster
            if (IsCanceled) return;
            onCloseModalHandler(
                modalsStateSetDict.resumeCustomerJourney.key,
                ModalsActions.RESUME,
                AlertType,
                AlertMessage,
            );
            setIsLoading(false);
        });
    };

    const makePublicCustomerJourney = () => {
        setIsLoading(true);
        const key = modalsStateSetDictKeys.makePublicCustomerJourney;
        const { allowMakePublicCustomerJourneys } = openedModalData;
        if (isEmpty(allowMakePublicCustomerJourneys)) {
            return onCloseModalHandler(key);
        }
        const allowMakePublicCustomerJourneysIds = allowMakePublicCustomerJourneys.map(({ workflowId }) => workflowId);
        doPostRequest(setPromotionIsPublicRequest.request, {
            queryString: {
                isPublic: true,
            },
            requestBody: {
                [PromotionTypesLabels.CustomerJourney]: allowMakePublicCustomerJourneysIds,
            },
            isShowError: false,
        }).then(({ AlertMessage, AlertType, IsCanceled }) => {
            // Aborted on unmount: do not close the modal or show a toaster
            if (IsCanceled) return;
            onCloseModalHandler(
                key,
                ModalsActions.MAKE_PUBLIC,
                AlertType,
                AlertMessage,
                allowMakePublicCustomerJourneysIds.length,
            );
            setIsLoading(false);
        });
    };

    const makePrivateCustomerJourney = () => {
        setIsLoading(true);
        const key = modalsStateSetDictKeys.makePrivateCustomerJourney;
        const { allowMakePrivateCustomerJourneys } = openedModalData;
        if (isEmpty(allowMakePrivateCustomerJourneys)) {
            return onCloseModalHandler(key);
        }
        const allowMakePrivateCustomerJourneysIds = allowMakePrivateCustomerJourneys.map(
            ({ workflowId }) => workflowId,
        );
        doPostRequest(setPromotionIsPublicRequest.request, {
            queryString: {
                isPublic: false,
            },
            requestBody: {
                [PromotionTypesLabels.CustomerJourney]: allowMakePrivateCustomerJourneysIds,
            },
            isShowError: false,
        }).then(({ AlertMessage, AlertType, IsCanceled }) => {
            // Aborted on unmount: do not close the modal or show a toaster
            if (IsCanceled) return;
            onCloseModalHandler(
                key,
                ModalsActions.MAKE_PRIVATE,
                AlertType,
                AlertMessage,
                allowMakePrivateCustomerJourneysIds.length,
            );
            setIsLoading(false);
        });
    };

    const isBulkAction = useMemo(() => {
        return (
            has(openedModalData, 'allowDeleteCustomerJourneys') ||
            has(openedModalData, 'allowArchiveCustomerJourneys') ||
            has(openedModalData, 'allowUnarchiveCustomerJourneys')
        );
    }, [openedModalData]);

    return isEmpty(openedModalData) ? null : (
        <>
            {isDeleteModalOpenedState && (
                <ConfirmationModal
                    onOk={isBulkAction ? deleteCustomerJourneys : deleteCustomerJourney}
                    isOkActive={isBulkAction ? !isEmpty(openedModalData.allowDeleteCustomerJourneys) : true}
                    onCancel={() => onCloseModalHandler(modalsStateSetDict.deleteCustomerJourney.key)}
                    isVisibleModal={isDeleteModalOpenedState}
                    titleText={isBulkAction ? t(l.DeleteSelectedCustomerJourneys) : t(l.DeleteCustomerJourney)}
                    isLoading={isLoading}
                    iconType={ActionsIcons.Delete}
                    actionLabel={t(l.Delete)}
                    className={ModalsClassNames.Delete}
                    allowDataList={openedModalData.allowDeleteCustomerJourneys}
                    notAllowDataList={openedModalData.notAllowDeleteCustomerJourneys}
                    allowDataLabel={l.AllowDeleteSelectedCustomerJourneys}
                    notAllowDataLabel={l.NotAllowDeleteSelectedCustomerJourneys}
                    questionLabel={
                        isBulkAction
                            ? l.DeleteSelectedCustomerJourneysQuestion
                            : t(l.AreYouSureYouWantToDeletCustomerJourney, {
                                  workflowName: `"${openedModalData.Name}"`,
                              })
                    }
                    isBulkAction={isBulkAction}
                />
            )}

            {isUsedInConfirmationModalOpenedState && (
                <ConfirmationModal
                    onOk={() => goToEditCustomerJourney?.(openedModalData?.workflow)}
                    onCancel={() => onCloseModalHandler(modalsStateSetDict.editCustomerJourney.key)}
                    isVisibleModal={isUsedInConfirmationModalOpenedState}
                    titleText={t(l.EditCustomerJourney)}
                    questionLabel={getUsedInConfirmationText(
                        t,
                        l.CustomerJourney,
                        openedModalData?.objects,
                        l.IsUsedInObjectsLabel,
                    )}
                    iconType={ActionsIcons.UsedIn}
                    actionLabel={t(l.Continue)}
                    className={ModalsClassNames.UsedIn}
                />
            )}

            {isArchiveModalOpenedState && (
                <ConfirmationModal
                    onOk={isBulkAction ? archiveCustomerJourneys : archiveCustomerJourney}
                    isOkActive={isBulkAction ? !isEmpty(openedModalData.allowArchiveCustomerJourneys) : true}
                    onCancel={() => onCloseModalHandler(modalsStateSetDict.archiveCustomerJourney.key)}
                    isVisibleModal={isArchiveModalOpenedState}
                    titleText={isBulkAction ? t(l.ArchiveSelectedCustomerJourneys) : t(l.ArchiveCustomerJourney)}
                    isLoading={isLoading}
                    iconType={ActionsIcons.Archive}
                    actionLabel={t(l.Archive)}
                    className={ModalsClassNames.Archive}
                    allowDataList={openedModalData.allowArchiveCustomerJourneys}
                    notAllowDataList={openedModalData.notAllowArchiveCustomerJourneys}
                    allowDataLabel={l.AllowArchiveSelectedCustomerJourneys}
                    notAllowDataLabel={l.NotAllowArchiveSelectedCustomerJourneys}
                    questionLabel={
                        isBulkAction
                            ? l.ArchiveSelectedCustomerJourneysQuestion
                            : t(l.AreYouSureYouWantToArchiveCustomerJourney, {
                                  workflowName: `"${openedModalData.Name}"`,
                              })
                    }
                    isBulkAction={isBulkAction}
                />
            )}

            {isUnArchiveModalOpenedState && (
                <ConfirmationModal
                    onOk={isBulkAction ? unarchiveCustomerJourneys : unarchiveCustomerJourney}
                    isOkActive={isBulkAction ? !isEmpty(openedModalData.allowUnarchiveCustomerJourneys) : true}
                    onCancel={() => onCloseModalHandler(modalsStateSetDict.unarchiveCustomerJourney.key)}
                    isVisibleModal={isUnArchiveModalOpenedState}
                    titleText={isBulkAction ? t(l.UnarchiveSelectedCustomerJourneys) : t(l.UnarchiveCustomerJourney)}
                    isLoading={isLoading}
                    iconType={ActionsIcons.UnArchive}
                    actionLabel={t(l.Unarchive)}
                    className={ModalsClassNames.UnArchive}
                    allowDataList={openedModalData.allowUnarchiveCustomerJourneys}
                    notAllowDataList={openedModalData.notAllowUnarchiveCustomerJourneys}
                    allowDataLabel={l.AllowUnarchiveSelectedCustomerJourneys}
                    notAllowDataLabel={l.NotAllowUnarchiveSelectedCustomerJourneys}
                    questionLabel={
                        isBulkAction
                            ? l.UnarchiveSelectedCustomerJourneysQuestion
                            : t(l.AreYouSureYouWantToUnarchiveCustomerJourney, {
                                  workflowName: `"${openedModalData.Name}"`,
                              })
                    }
                    isBulkAction={isBulkAction}
                />
            )}

            {isStopSendModalOpenedState && (
                <ConfirmationModal
                    onOk={stopSendCustomerJourney}
                    onCancel={() => onCloseModalHandler(modalsStateSetDict.stopSendCustomerJourney.key)}
                    isVisibleModal={isStopSendModalOpenedState}
                    titleText={t(isActionCampaign ? l.StopActionCampaign : l.StopCustomerJourney)}
                    questionLabel={t(
                        isActionCampaign
                            ? l.AreYouSureYouWantToStopActionCampaign
                            : l.AreYouSureYouWantToStopCustomerJourney,
                        {
                            workflowName: `"${openedModalData.Name}"`,
                        },
                    )}
                    isLoading={isLoading}
                    iconType={ActionsIcons.Stop}
                    actionLabel={t(l.Stop)}
                    className={ModalsClassNames.Stop}
                />
            )}

            {isStartCustomerJourneyModalOpenedState && (
                <ConfirmationModalWithWarning
                    onOk={startCustomerJourney}
                    onCancel={() => onCloseModalHandler(modalsStateSetDict.startCustomerJourney.key)}
                    titleText={t(isActionCampaign ? l.StartActionCampaign : l.StartCustomerJourney)}
                    questionLabel={t(
                        isActionCampaign
                            ? l.AreYouSureYouWantToStartActionCampaign
                            : l.AreYouSureYouWantToStartCustomerJourney,
                        {
                            workflowName: `"${openedModalData.Name}"`,
                        },
                    )}
                    isLoading={isLoading}
                    iconType={ActionsIcons.Start}
                    actionLabel={t(l.Start)}
                    cancelLabel={t(l.Cancel)}
                    promotionType={openedModalData.PromotionType}
                />
            )}

            {isResumeModalOpenedState && (
                <ConfirmationModalWithWarning
                    onOk={resumeCustomerJourney}
                    onCancel={() => onCloseModalHandler(modalsStateSetDict.resumeCustomerJourney.key)}
                    titleText={t(l.ResumeCustomerJourney)}
                    questionLabel={t(l.AreYouSureYouWantToResumeCustomerJourney, {
                        workflowName: `"${openedModalData.Name}"`,
                    })}
                    isLoading={isLoading}
                    iconType={ActionsIcons.Resume}
                    actionLabel={t(l.Resume)}
                    cancelLabel={t(l.Cancel)}
                    promotionType={openedModalData.PromotionType}
                />
            )}

            {isInfoModalOpenedState && (
                <CustomerJourneyInfoModal
                    workflowId={openedModalData.WorkflowId}
                    promotionType={openedModalData.PromotionType}
                    handleClose={() => onCloseModalHandler(modalsStateSetDict.infoCustomerJourney.key)}
                    isVisible={isInfoModalOpenedState}
                    hasVisualization={openedModalData.HasVisualization}
                    handleInfoModalActionClick={handleInfoModalActionClick}
                />
            )}

            {isMakePublicModalOpenedState && (
                <ConfirmationModal
                    isVisibleModal={true}
                    isBulkAction={true}
                    onOk={makePublicCustomerJourney}
                    isOkActive={!isEmpty(openedModalData.allowMakePublicCustomerJourneys)}
                    onCancel={() => onCloseModalHandler(modalsStateSetDictKeys.makePublicCustomerJourney)}
                    titleText={t(l.MakePublicSelectedCustomerJourneys)}
                    isLoading={isLoading}
                    actionLabel={t(l.MakePublic)}
                    allowDataList={openedModalData.allowMakePublicCustomerJourneys}
                    notAllowDataList={openedModalData.notAllowMakePublicCustomerJourneys}
                    allowDataLabel={l.AllowMakePublicSelectedCustomerJourneys}
                    notAllowDataLabel={l.NotAllowMakePublicSelectedCustomerJourneys}
                    questionLabel={l.MakePublicSelectedCustomerJourneysQuestion}
                />
            )}

            {isMakePrivateModalOpenedState && (
                <ConfirmationModal
                    isVisibleModal={true}
                    isBulkAction={true}
                    onOk={makePrivateCustomerJourney}
                    isOkActive={!isEmpty(openedModalData.allowMakePrivateCustomerJourneys)}
                    onCancel={() => onCloseModalHandler(modalsStateSetDictKeys.makePrivateCustomerJourney)}
                    titleText={t(l.MakePrivateSelectedCustomerJourneys)}
                    isLoading={isLoading}
                    actionLabel={t(l.MakePrivate)}
                    allowDataList={openedModalData.allowMakePrivateCustomerJourneys}
                    notAllowDataList={openedModalData.notAllowMakePrivateCustomerJourneys}
                    allowDataLabel={l.AllowMakePrivateSelectedCustomerJourneys}
                    notAllowDataLabel={l.NotAllowMakePrivateSelectedCustomerJourneys}
                    questionLabel={l.MakePrivateSelectedCustomerJourneysQuestion}
                />
            )}
        </>
    );
};

export default memo(CustomerJourneyActionsModals);
